import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BlahService } from '@headpower/blah-ng';
import { Observable } from 'rxjs';
import { AppConfig } from '../app.config';
import { ToasterService } from '@headpower/layout';

interface ChatMessage {
    isOwn: boolean;
    text: string;
    threadId?: string;
}

interface ChatData {
    placeholder: string;
    senderName: string;
    senderPicture: string;
    infoText: string;
    receivedMessageFooter: string;
    messageHistory: ChatMessage[];
    messageMaxLength: number;
    loading: boolean;
    floatingButtonPosition?: FloatingButtonPosition;
}

interface FloatingButtonPosition {
    right: string;
    bottom: string;
}

@Injectable()
export class FloatingChatService {
    private readonly assistantApiUrl: string =
        this.config.backendAiUri + 'Assistant/api/';
    private readonly maxHistoryLengthSplice: number = -10;
    private readonly maxMessageLength: number = 500;
    private readonly defaultModel: string = 'gpt-4o';
    private readonly itemName: string = 'chatData';

    public chatData: ChatData;
    public chatVisible: boolean = true;

    constructor(
        private readonly blahService: BlahService,
        private readonly http: HttpClient,
        private readonly config: AppConfig,
        private readonly toaster: ToasterService
    ) {
        // Save chat data to session storage before page unloads
        window.addEventListener('beforeunload', () => {
            sessionStorage.setItem(this.itemName, JSON.stringify(this.chatData));
        });

        // Retrieve chat data from session storage if exists
        const savedData = sessionStorage.getItem(this.itemName);
        if (savedData) {
            this.chatData = JSON.parse(savedData);
        } else {
            this.chatData = this.getInitialChatData();
        }

        this.blahService.localeChange$.subscribe(() => {
            this.chatData.placeholder = this.blahService.blah(
                'floatingChat.placeholder'
            );
            this.chatData.infoText = this.blahService.blah(
                'floatingChat.infoText'
            );
            this.chatData.receivedMessageFooter = this.blahService.blah(
                'floatingChat.receivedMessageFooter'
            );
            this.chatData.messageHistory[0].text = this.blahService.blah(
                'floatingChat.initialMessage'
            );
        });
    }

    public getInitialChatData(): ChatData {
        return {
            placeholder: this.blahService.blah('floatingChat.placeholder'),
            senderName: this.blahService.blah('floatingChat.senderName'),
            senderPicture:
                'https://static.headpower.fi/images/ai/helmi_head.png',
            infoText: this.blahService.blah('floatingChat.infoText'),
            receivedMessageFooter: this.blahService.blah(
                'floatingChat.receivedMessageFooter'
            ),
            messageHistory: [
                {
                    text: this.blahService.blah('floatingChat.initialMessage'),
                    isOwn: false,
                },
            ],
            messageMaxLength: this.maxMessageLength,
            loading: false,
            floatingButtonPosition: { bottom: '8rem', right: '2.8rem' },
        };
    }

    public handleChatMessageSent(event: string) {
        let responseReceived = false;
        let messageAdded = false;

        const addMessage = (text: string, isOwn: boolean) => {
            if (!messageAdded) {
                messageAdded = true;

                if (this.chatData.messageHistory.filter(t => t.threadId).length > 0) {
                    this.chatData.messageHistory.push({ text, isOwn, threadId: this.chatData.messageHistory.filter(t => t.threadId)[0].threadId });
                } else {
                    this.chatData.messageHistory.push({ text, isOwn });
                }
            }
        };

        // Limit sent message history, and exclude the default introduction message from history
        this.postChatMessage(
            event,
            this.chatData.messageHistory.slice(1).slice(this.maxHistoryLengthSplice)
        ).subscribe({
            next: (o) => {
                responseReceived = true;
                addMessage(event, true);
                this.chatData.loading = false;
                this.chatData.messageHistory.push({
                    text: o.response,
                    isOwn: false,
                    threadId: o.threadId,
                });
            },
            error: () => {
                responseReceived = true;
                addMessage(event, true);
                this.chatData.loading = false;
                this.toaster.create(
                    this.blahService.blah('floatingChat.error')
                );
            },
        });

        setTimeout(() => addMessage(event, true), 200);
        setTimeout(() => {
            if (!responseReceived) this.chatData.loading = true;
        }, 500);
    }

    public handleChatReset() {
        this.chatData = this.getInitialChatData();
    }

    public postChatMessage(
        text: string,
        history: ChatMessage[]
    ): Observable<any> {
        const url = this.assistantApiUrl + 'Assistant';

        const payload = {
            message: text,
            history: history,
            model: this.defaultModel,
        };

        return this.http.post(url, payload);
    }

    public showChat() {
        this.chatVisible = true;   
    }

    public hideChat() {
        this.chatVisible = false;
    }
}
